import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
};

export type ColumnPinSide = 'left' | 'right' | null;

type TradesColumnStore = {
  columns: ColumnConfig[];
  pinnedLeft: string[];
  pinnedRight: string[];
  setColumns: (columns: ColumnConfig[]) => void;
  toggleColumnVisibility: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  resetColumns: () => void;
  isColumnsDefault: () => boolean;
};

export const defaultColumns: ColumnConfig[] = [
  { id: 'id', label: 'ID', visible: true },
  { id: 'status_display', label: 'Status', visible: true },
  { id: 'symbol_id', label: 'Symbol', visible: true },
  { id: 'description', label: 'Description', visible: true },
  { id: 'isin', label: 'ISIN', visible: true },
  { id: 'counterparty', label: 'Counterparty', visible: true },
  { id: 'agreement_display', label: 'Agreement', visible: true },
  { id: 'trade_type_display', label: 'Trade Type', visible: true },
  { id: 'term_type_display', label: 'Trade Term', visible: true },
  { id: 'quantity', label: 'Quantity', visible: true },
  // LR-522: price/value columns grouped by stage — Trade → COB → Last Marked —
  // each exposing Gross Price / (FX Rate) / Margin Price / Gross Value / Margin
  // Value. The v10 migrate below reorders returning users' persisted columns into
  // this same grouping. The retired `total_value` column is dropped (its concept
  // is COB Gross Value, shipped in LR-521); getTradeTotalValue still feeds MetricsBar.
  //
  // Trade stage (booking figures entered at Trade Entry)
  { id: 'gross_price', label: 'Trade Gross Price', visible: true },
  { id: 'fx_rate', label: 'Trade FX Rate', visible: true },
  { id: 'margin_price', label: 'Trade Margin Price', visible: true },
  { id: 'trade_gross_value', label: 'Trade Gross Value', visible: true },
  { id: 'contract_value', label: 'Trade Margin Value', visible: true },
  // COB stage (close-of-business T-1) — LR-521
  { id: 'cob_price', label: 'COB Gross Price', visible: true },
  // LR-494: COB (T-1 close) FX rate, security → settlement; cross-ccy rows only.
  { id: 'cob_fx_rate', label: 'COB FX Rate', visible: true },
  { id: 'cob_margin_price', label: 'COB Margin Price', visible: true },
  { id: 'cob_gross_value', label: 'COB Gross Value', visible: true },
  { id: 'cob_margin_value', label: 'COB Margin Value', visible: true },
  // Live reference (real-time market snapshot)
  { id: 'current_market_price', label: 'Current Market Price', visible: true },
  // Last Marked stage (latest MTM repricing; blank until first marked)
  { id: 'latest_mark_price', label: 'Last Marked Gross Price', visible: true },
  { id: 'marked_margin_price', label: 'Last Marked Margin Price', visible: true },
  { id: 'last_marked_gross_value', label: 'Last Marked Gross Value', visible: true },
  { id: 'marked_contract_value', label: 'Last Marked Margin Value', visible: true },
  // Margin reference + earnings
  { id: 'margin', label: 'Margin %', visible: true },
  { id: 'daily_earnings', label: 'Daily Earnings', visible: true },
  { id: 'trade_date', label: 'Trade Date', visible: true },
  { id: 'settlement_date', label: 'Open Settlement Date', visible: true },
  { id: 'actual_settlement_date', label: 'Close Settlement Date', visible: true },
  { id: 'maturity_date', label: 'Maturity Date', visible: true },
  // Tab-specific columns (hidden by default, shown via tabs)
  { id: 'custodian_name', label: 'Custodian', visible: false },
  { id: 'rate_entry_mode', label: 'Rate Mode', visible: false },
  { id: 'benchmark_name', label: 'Benchmark', visible: false },
  { id: 'total_rate', label: 'Total Rate', visible: false },
  { id: 'spread_bps', label: 'Spread (bps)', visible: false },
  { id: 'fee_percent', label: 'Fee %', visible: false },
  // LR-424: earnings columns previously missing from the column manager — they
  // were rendered (appended as "new columns") but not surfaceable in the picker.
  { id: 'rate_label', label: 'Rate Label', visible: false },
  // LR-631: label only - the id keys saved layouts and must not move.
  { id: 'cost_of_collateral', label: 'Cash Pool Interest Accrual', visible: true },
  { id: 'fee_income', label: 'Fee Income', visible: true },
  { id: 'net_amount', label: 'Net Amount', visible: true },
  { id: 'closing_cash', label: 'Closing Cash', visible: true },
  { id: 'delivery_type', label: 'Delivery Type', visible: false },
  { id: 'settlement_venue', label: 'Settlement Venue', visible: false },
  // LR-404: per-leg settlement markers, hidden by default (opt-in for FOP-heavy users).
  { id: 'cash_settled', label: 'Cash Settlement', visible: false },
  { id: 'securities_settled', label: 'Securities Settlement', visible: false },
  { id: 'booking_center', label: 'Booking Center', visible: false },
  { id: 'custodian_account', label: 'Custodian Account', visible: false },
  { id: 'counterparty_account', label: 'Counterparty Account', visible: false },
  { id: 'collateral_type', label: 'Collateral Type', visible: false },
  // LR-424: collateral detail columns previously missing from the column manager.
  { id: 'collateral_isin', label: 'Collateral ISIN', visible: true },
  { id: 'collateral_quantity', label: 'Collateral Qty', visible: true },
  { id: 'collateral_value', label: 'Collateral Value', visible: true },
  { id: 'pre_delivery_party', label: 'Pre-Delivery', visible: true },
  // LR-474: 'Currency' → 'Security Ccy' (label feeds the column menu AND the CSV
  // header); id stays `currency` — persisted column state, no migration needed.
  { id: 'currency', label: 'Security Ccy', visible: true },
  { id: 'settlement_currency', label: 'Settlement Ccy', visible: true },
  { id: 'created_at', label: 'Created At', visible: false },
  { id: 'updated_at', label: 'Updated At', visible: false },
];

// Exported for the saved-views adapter (LR-671): an empty payload resets pins here.
export const DEFAULT_PINNED_LEFT: string[] = [];
export const DEFAULT_PINNED_RIGHT: string[] = [];

export const useTradesColumnStore = create<TradesColumnStore>()(
  devtools(
  persist(
    (set, get) => ({
      columns: defaultColumns,
      pinnedLeft: DEFAULT_PINNED_LEFT,
      pinnedRight: DEFAULT_PINNED_RIGHT,
      setColumns: (columns: ColumnConfig[]) => set({ columns }),
      toggleColumnVisibility: (columnId: string) =>
        set((state: TradesColumnStore) => {
          const col = state.columns.find((c) => c.id === columnId);
          const becomingHidden = col?.visible === true;
          return {
            columns: state.columns.map((c: ColumnConfig) =>
              c.id === columnId ? { ...c, visible: !c.visible } : c
            ),
            // Auto-unpin when hiding
            pinnedLeft: becomingHidden
              ? state.pinnedLeft.filter((id) => id !== columnId)
              : state.pinnedLeft,
            pinnedRight: becomingHidden
              ? state.pinnedRight.filter((id) => id !== columnId)
              : state.pinnedRight,
          };
        }),
      showAllColumns: () =>
        set((state: TradesColumnStore) => ({
          columns: state.columns.map((c) => ({ ...c, visible: true })),
        })),
      hideAllColumns: () =>
        set((state: TradesColumnStore) => ({
          columns: state.columns.map((c) => ({ ...c, visible: false })),
          pinnedLeft: [],
          pinnedRight: [],
        })),
      reorderColumns: (fromIndex: number, toIndex: number) =>
        set((state: TradesColumnStore) => {
          const newColumns = [...state.columns];
          const [movedColumn] = newColumns.splice(fromIndex, 1);
          newColumns.splice(toIndex, 0, movedColumn);
          return { columns: newColumns };
        }),
      pinColumn: (columnId: string, side: ColumnPinSide) =>
        set((state: TradesColumnStore) => {
          if (columnId === 'rowNumber') return state;
          const newLeft = state.pinnedLeft.filter((id) => id !== columnId);
          const newRight = state.pinnedRight.filter((id) => id !== columnId);
          if (side === 'left') newLeft.push(columnId);
          if (side === 'right') newRight.push(columnId);
          return { pinnedLeft: newLeft, pinnedRight: newRight };
        }),
      getPinState: (columnId: string): ColumnPinSide => {
        const state = get();
        if (state.pinnedLeft.includes(columnId)) return 'left';
        if (state.pinnedRight.includes(columnId)) return 'right';
        return null;
      },
      resetColumns: () =>
        set({
          columns: defaultColumns,
          pinnedLeft: DEFAULT_PINNED_LEFT,
          pinnedRight: DEFAULT_PINNED_RIGHT,
        }),
      isColumnsDefault: (): boolean => {
        const state: TradesColumnStore = get();
        if (state.columns.length !== defaultColumns.length) return false;
        const leftMatch = state.pinnedLeft.length === DEFAULT_PINNED_LEFT.length &&
          state.pinnedLeft.every((id, i) => id === DEFAULT_PINNED_LEFT[i]);
        const rightMatch = state.pinnedRight.length === DEFAULT_PINNED_RIGHT.length &&
          state.pinnedRight.every((id, i) => id === DEFAULT_PINNED_RIGHT[i]);
        if (!leftMatch || !rightMatch) return false;
        return state.columns.every(
          (col: ColumnConfig, idx: number) =>
            col.id === defaultColumns[idx].id && col.visible === defaultColumns[idx].visible
        );
      },
    }),
    {
      name: 'trades-columns-storage',
      version: 10,
      // LR-345 round 2: drop the `haircut` column entirely. Persisted entries are
      // stripped here; `merge` below also filters by `defaultById.has(id)` as a
      // belt-and-suspenders guard.
      // LR-406: rename label of `settlement_date` and inject the new
      // `actual_settlement_date` column right after it so the column order is
      // preserved for existing users (the default `merge` appends new columns
      // at the end, which would push the close-settlement cell to the far
      // right — not where it belongs visually).
      // LR-306 (v6): replace the obsolete `last_marked_price` orphan entry
      // (never had a matching accessor) with the new `latest_mark_price`
      // column and inject `total_value` next to it, anchored on
      // `current_market_price` so existing users get the new columns right
      // after Current Market Price instead of appended at the end.
      // LR-466 (v7): rename the `daily_earnings_usd` column id to `daily_earnings`
      // (the `_usd` suffix was a misnomer — the figure is in the trade's own
      // currency, not USD). Renamed IN PLACE so a returning user keeps the
      // column's position + visibility; without this step `merge` would drop the
      // now-unknown `daily_earnings_usd` and re-append `daily_earnings` at the far
      // right with visibility reset to the default (the same far-right regression
      // the v5/v6 steps above were written to avoid).
      migrate: (persistedState: unknown, version: number) => {
        if (!persistedState || typeof persistedState !== 'object') return persistedState;
        const state = persistedState as { columns?: ColumnConfig[] };
        if (version < 4) {
          if (Array.isArray(state.columns)) {
            state.columns = state.columns.filter((c) => c.id !== 'haircut');
          }
        }
        if (version < 5 && Array.isArray(state.columns)) {
          const settlementIdx = state.columns.findIndex((c) => c.id === 'settlement_date');
          if (settlementIdx !== -1) {
            state.columns[settlementIdx].label = 'Open Settlement Date';
            const hasActual = state.columns.some((c) => c.id === 'actual_settlement_date');
            if (!hasActual) {
              state.columns.splice(settlementIdx + 1, 0, {
                id: 'actual_settlement_date',
                label: 'Close Settlement Date',
                visible: true,
              });
            }
          }
        }
        if (version < 6 && Array.isArray(state.columns)) {
          const oldIdx = state.columns.findIndex((c) => c.id === 'last_marked_price');
          const priorVisible = oldIdx !== -1 ? state.columns[oldIdx].visible : true;
          if (oldIdx !== -1) state.columns.splice(oldIdx, 1);
          const currentIdx = state.columns.findIndex((c) => c.id === 'current_market_price');
          if (currentIdx !== -1) {
            if (!state.columns.some((c) => c.id === 'latest_mark_price')) {
              state.columns.splice(currentIdx + 1, 0, {
                id: 'latest_mark_price',
                label: 'Last Market Price',
                visible: priorVisible,
              });
            }
            const latestIdx = state.columns.findIndex((c) => c.id === 'latest_mark_price');
            if (latestIdx !== -1 && !state.columns.some((c) => c.id === 'total_value')) {
              state.columns.splice(latestIdx + 1, 0, {
                id: 'total_value',
                label: 'Total Value',
                visible: true,
              });
            }
          }
        }
        // v7 migration combines two independent column changes:
        //  - LR-474: inject the new Settlement Ccy column right after the (renamed)
        //    Security Ccy column instead of letting `merge` append it at the far
        //    right. The label rename itself needs no migration — merge refreshes
        //    labels from defaults.
        //  - origin/main: rename the daily_earnings_usd column id/label to
        //    daily_earnings (the field was renamed backend-side).
        if (version < 7 && Array.isArray(state.columns)) {
          const currencyIdx = state.columns.findIndex((c) => c.id === 'currency');
          if (currencyIdx !== -1 && !state.columns.some((c) => c.id === 'settlement_currency')) {
            state.columns.splice(currencyIdx + 1, 0, {
              id: 'settlement_currency',
              label: 'Settlement Ccy',
              visible: true,
            });
          }
          const dailyIdx = state.columns.findIndex((c) => c.id === 'daily_earnings_usd');
          if (dailyIdx !== -1) {
            state.columns[dailyIdx] = { ...state.columns[dailyIdx], id: 'daily_earnings', label: 'Daily Earnings' };
          }
        }
        // LR-494 (v8): inject the new COB FX Rate column right after COB Price
        // (T-1) instead of letting `merge` append it at the far right.
        if (version < 8 && Array.isArray(state.columns)) {
          const cobIdx = state.columns.findIndex((c) => c.id === 'cob_price');
          if (cobIdx !== -1 && !state.columns.some((c) => c.id === 'cob_fx_rate')) {
            state.columns.splice(cobIdx + 1, 0, {
              id: 'cob_fx_rate',
              label: 'COB FX Rate',
              visible: true,
            });
          }
        }
        // LR-521 (v9): inject COB Margin Price + COB Gross/Margin Value right
        // after COB FX Rate so the COB group stays together, instead of `merge`
        // appending them at the far right. The "COB Price (T-1)" → "COB Gross
        // Price" label rename needs no step here — `merge` refreshes labels from
        // defaults. New columns are inserted only if absent (idempotent).
        if (version < 9 && Array.isArray(state.columns)) {
          const anchorId = state.columns.some((c) => c.id === 'cob_fx_rate')
            ? 'cob_fx_rate'
            : 'cob_price';
          const anchorIdx = state.columns.findIndex((c) => c.id === anchorId);
          if (anchorIdx !== -1) {
            const toAdd = [
              { id: 'cob_margin_price', label: 'COB Margin Price', visible: true },
              { id: 'cob_gross_value', label: 'COB Gross Value', visible: true },
              { id: 'cob_margin_value', label: 'COB Margin Value', visible: true },
            ].filter((col) => !state.columns!.some((c) => c.id === col.id));
            state.columns.splice(anchorIdx + 1, 0, ...toAdd);
          }
        }
        // LR-522 (v10): reclassify the Trades price/value columns into three
        // stages — Trade / COB / Last Marked — each with Gross Price, (FX Rate,)
        // Margin Price, Gross Value, Margin Value. Unlike the splice-at-anchor
        // steps above, this REORDERS the whole cluster (merge preserves persisted
        // order, so editing defaultColumns alone would NOT reorder returning
        // users). It injects the six ids new to the blotter store (gross_price —
        // the former MtM-only Contract Price def, now shown here; fx_rate;
        // trade_gross_value; marked_margin_price; last_marked_gross_value;
        // marked_contract_value) and DROPS the retired `total_value` column (its
        // concept is COB Gross Value, shipped in LR-521; getTradeTotalValue still
        // feeds MetricsBar). Every surviving column keeps its visibility; the new
        // ids default to visible. Labels are refreshed from defaults by `merge`.
        // Idempotent: the cluster is stripped then rebuilt from a fixed order, so
        // re-running produces no dupes and always drops a stray total_value.
        if (version < 10 && Array.isArray(state.columns)) {
          const CLUSTER_ORDER = [
            // Trade
            'gross_price', 'fx_rate', 'margin_price', 'trade_gross_value', 'contract_value',
            // COB
            'cob_price', 'cob_fx_rate', 'cob_margin_price', 'cob_gross_value', 'cob_margin_value',
            // Live reference
            'current_market_price',
            // Last Marked
            'latest_mark_price', 'marked_margin_price', 'last_marked_gross_value', 'marked_contract_value',
          ];
          const LABELS: Record<string, string> = {
            gross_price: 'Trade Gross Price',
            fx_rate: 'Trade FX Rate',
            margin_price: 'Trade Margin Price',
            trade_gross_value: 'Trade Gross Value',
            contract_value: 'Trade Margin Value',
            cob_price: 'COB Gross Price',
            cob_fx_rate: 'COB FX Rate',
            cob_margin_price: 'COB Margin Price',
            cob_gross_value: 'COB Gross Value',
            cob_margin_value: 'COB Margin Value',
            current_market_price: 'Current Market Price',
            latest_mark_price: 'Last Marked Gross Price',
            marked_margin_price: 'Last Marked Margin Price',
            last_marked_gross_value: 'Last Marked Gross Value',
            marked_contract_value: 'Last Marked Margin Value',
          };
          // Strip the whole cluster + the retired total_value, then rebuild the
          // cluster in canonical order at the position it used to occupy (after
          // the non-cluster columns that preceded the first cluster member).
          const STRIP = new Set([...CLUSTER_ORDER, 'total_value']);
          const priorVisible = new Map(
            state.columns.filter((c) => STRIP.has(c.id)).map((c) => [c.id, c.visible] as const),
          );
          let insertAt = 0;
          for (const c of state.columns) {
            if (STRIP.has(c.id)) break;
            insertAt++;
          }
          const rest = state.columns.filter((c) => !STRIP.has(c.id));
          const rebuilt: ColumnConfig[] = CLUSTER_ORDER.map((id) => ({
            id,
            label: LABELS[id],
            visible: priorVisible.has(id) ? priorVisible.get(id)! : true,
          }));
          rest.splice(Math.min(insertAt, rest.length), 0, ...rebuilt);
          state.columns = rest;
        }
        return persistedState;
      },
      merge: (persistedState: unknown, currentState: TradesColumnStore): TradesColumnStore => {
        const persisted =
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as Partial<TradesColumnStore>)
            : undefined;
        if (!persisted?.columns?.length) return currentState;
        const persistedById = new Map(persisted.columns.map((c: ColumnConfig) => [c.id, c]));
        const defaultById = new Map(defaultColumns.map((c: ColumnConfig) => [c.id, c]));
        const mergedColumns: ColumnConfig[] = persisted.columns
          .filter((pc: ColumnConfig) => defaultById.has(pc.id))
          .map((pc: ColumnConfig) => ({ ...defaultById.get(pc.id)!, visible: pc.visible }));
        for (const dc of defaultColumns) {
          if (!persistedById.has(dc.id)) {
            mergedColumns.push(dc);
          }
        }
        // Prune pins for ids that no longer exist (column removed in a later
        // release) — mirrors the MTM merge, so a stale pin can never reference
        // a missing column (LR-474 r1 #4).
        const knownIds = new Set(mergedColumns.map((c) => c.id));
        const keepPins = (pins: unknown): string[] =>
          Array.isArray(pins) ? (pins as string[]).filter((id) => knownIds.has(id)) : [];
        return {
          ...currentState,
          ...persisted,
          columns: mergedColumns,
          pinnedLeft: keepPins(persisted.pinnedLeft),
          pinnedRight: keepPins(persisted.pinnedRight),
        };
      },
    }
  ),
  { name: 'trades-column-store', enabled: import.meta.env.DEV }
  )
);
